import { Component, OnInit, ViewChild } from '@angular/core';
import { PagedResultGeneric } from 'src/app/shared/model/paged-result.model';
import { TaskDto } from 'src/app/tasks/shared/dto/task.dto';
import { DatagridService, TranslationService } from 'src/app/shared';
import { Table } from 'primeng/table';
import { MenuItem, ConfirmationService, SelectItem } from 'primeng/api';
import { WlUpperCaseFirstLetterPipe } from 'src/app/shared/pipes/ucfirst.pipe';
import { TaskService } from 'src/app/tasks/shared/task.service';
import { TaskSearchDto } from 'src/app/tasks/shared/dto/task-search.dto';
import { TaskEditDialogComponent } from 'src/app/tasks/task-edit-dialog/task-edit-dialog.component';

@Component({
  selector: 'wl-dashboard-tasks',
  templateUrl: './dashboard-tasks.component.html',
  styleUrls: ['./dashboard-tasks.component.less'],
    standalone: false
})
export class DashboardTasksComponent implements OnInit {
  public search: TaskSearchDto;
  public searchResult: PagedResultGeneric<TaskDto>;
  public loading: boolean;
  public rowMenuItems: Map<number, MenuItem[]>;
  public acceptText: string;
  public rejectText: string;
  public pageSizeOptions: Array<SelectItem>;
  public selectedPageSize: number;

  @ViewChild(Table) myOppsGrid: Table;
  @ViewChild(TaskEditDialogComponent) taskEditDialog: TaskEditDialogComponent;

  constructor(
    private _datagridService: DatagridService,
    private _ucFirstPipe: WlUpperCaseFirstLetterPipe,
    private _translationService: TranslationService,
    private _confirmationService: ConfirmationService,
    private _taskService: TaskService
  ) {
    this.rowMenuItems = new Map();
  }

  ngOnInit(): void {
    Promise.all([
      this._translationService.getTitlecase('common.page-size.show-10'),
      this._translationService.getTitlecase('common.page-size.show-all'),
    ]).then((data: string[]) => {
      this.pageSizeOptions = [
        { label: data[0], value: 10 },
        { label: data[1], value: 1001 },
      ];
    });

    this.search = {
      PageSize: 10,
      PageNumber: 1,
      OrderBy: 'DateDue',
      TotalPages: 0,
      TotalCount: 0,
    } as TaskSearchDto;

    this.loadTasks();
  }

  public lazyLoadTasks($event) {
    if (this.search) {
      this._datagridService.mapDatagridPagingToPagedSearch($event, this.search);

      this.loadTasks();
    }
  }

  actionItemsForTask(task: TaskDto): MenuItem[] {
    if (!this.rowMenuItems.has(task.TaskId)) {
      this.rowMenuItems.set(task.TaskId, [
        {
          label: this._ucFirstPipe.transform(this._translationService.getInstant('common.edit')),
          icon: 'pi pi-pencil',
          command: () => this.launchEditDialog(task.TaskId),
        },
        {
          label: this._ucFirstPipe.transform(this._translationService.getInstant('common.complete')),
          icon: 'pi pi-check-square',
          command: () => this.closeTask(task),
        },
        {
          label: this._ucFirstPipe.transform(this._translationService.getInstant('common.delete')),
          icon: 'pi pi-times',
          command: () => this.deleteTask(task.TaskId),
        },
      ]);
    }
    return this.rowMenuItems.get(task.TaskId);
  }

  public launchEditDialog(taskId: number) {
    this.taskEditDialog.open(taskId);
  }

  public closeTask(task: TaskDto) {
    task.Closed = true;
    task.DateClosed = new Date();
    this._taskService.update(task).then(() => this.loadTasks(true));
  }

  public deleteTask(id: number) {
    this.rejectText = this._translationService.getInstant('common.cancel').toUpperCase();
    this.acceptText = this._translationService.getInstant('common.delete').toUpperCase();

    this._confirmationService.confirm({
      key: 'deleteTaskCD',
      header: this._translationService.getInstantTitlecase('dashboard.tasks.delete-task-header'),
      message: this._translationService.getInstantCapitalizeFirstCharacter('dashboard.tasks.delete-task-body'),
      accept: () => {
        this._taskService.delete(id).then(() => {
          this.loadTasks(true);
        });
      },
      reject: () => {
        this._confirmationService.close();
      },
    });
  }

  public handlePageSizeChange($event) {
    if ($event) {
      this.search.PageSize = $event;
      this.myOppsGrid.rows = $event;
      this.lazyLoadTasks({ first: 0, rows: $event });
    }
  }

  public loadTasks(forceRefresh = false) {
    this.loading = true;
    this._taskService
      .getMyTasks(this.search, forceRefresh)
      .then((data: PagedResultGeneric<TaskDto>) => {
        this.searchResult = data;
      })
      .finally(() => {
        this.loading = false;
      });
  }
}
